import { SafePromise } from '@benzinga/safe-await';
import { ProductPlan, Product } from './entities';

export interface IngressProductFeature {
  available_packages: string[];
  base_price: string;
  description?: string;
  is_hidden?: boolean;
  name: string;
  slug: string;
  uuid: string;
}

export interface IngressProductPlan {
  interval: string;
  interval_count: number;
  name: string;
  package: string;
  price: string;
  slug: string;
  trial_period_days: number;
  uuid: string;
}

export interface IngressProductPackage {
  checkout_url: string;
  description: string;
  features: string[];
  name: string;
  plans: IngressProductPlan[];
  slug: string;
  uuid: string;
}

export interface IngressProduct {
  features: IngressProductFeature[];
  name: string;
  packages: IngressProductPackage[];
  plans: IngressProductPlan[];
  premium_features: IngressProductFeature[];
  slug: string;
  uuid: string;
}

export const ingestProduct = async (incomingProduct: SafePromise<IngressProduct>): SafePromise<Product> => {
  const productResult = await incomingProduct;

  if (productResult.err) {
    return { err: productResult.err };
  } else {
    const product = productResult.ok;
    return {
      ok: {
        features: product.features.map(f => ({
          availablePackages: f.available_packages,
          basePrice: f.base_price,
          description: f.description,
          isHidden: f.is_hidden,
          name: f.name,
          slug: f?.slug,
          uuid: f.uuid,
        })),
        name: product.name,
        packages: product.packages.map(p => ({
          ...p,
          checkoutUrl: p.checkout_url,
          plans: p.plans.map<ProductPlan>(
            f =>
              ({
                interval: f.interval as 'year' | 'month',
                intervalCount: f.interval_count as 1 | 3 | 24,
                name: f.name,
                package: f.package,
                price: f.price,
                slug: f?.slug,
                trialPeriodDays: f.trial_period_days,
                uuid: f.uuid,
              }) as unknown as ProductPlan,
          ),
        })),
        plans: product.plans.map<ProductPlan>(
          f =>
            ({
              interval: f.interval as 'year' | 'month',
              intervalCount: f.interval_count as 1 | 3 | 24,
              name: f.name,
              package: f.package,
              price: f.price,
              slug: f?.slug,
              trialPeriodDays: f.trial_period_days,
              uuid: f.uuid,
            }) as unknown as ProductPlan,
        ),
        premiumFeatures: product.premium_features.map(f => ({
          availablePackages: f.available_packages,
          basePrice: f.base_price,
          description: f.description,
          isHidden: f.is_hidden,
          name: f.name,
          slug: f?.slug,
          uuid: f.uuid,
        })),
        slug: product?.slug,
        uuid: product.uuid,
      },
    };
  }
};
